// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: what the browser shows about the one channel tree (nested-ordering-plan.md 6.4): "Parent > Child"
// labels, tree order for lists and pickers, what a category delete does, and a category's whole subtree for
// mark as read. Pure: callers pass the guild's tree (guildChannelTree). Exclude from upstream sync.

import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {
	buildGuildChannelTree,
	type ChannelTree,
	channelPathLabel,
	channelSubtreeIds,
	formatChannelPath,
	MAX_CHANNEL_TREE_DEPTH,
} from '@fluxer/schema/src/domains/channel/GuildChannelTree';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export type GuildChannelTree = ChannelTree<string, Channel>;

/** Picker labels are shortened from the left beyond this, so the immediate parent stays visible. */
export const CHANNEL_PATH_MAX_CHARS = 60;

export function buildChannelTree(channels: ReadonlyArray<Channel>): GuildChannelTree {
	return buildGuildChannelTree<string, Channel>(channels);
}

export function guildChannelTree(guildId: string): GuildChannelTree {
	return buildChannelTree(Channels.getGuildChannels(guildId));
}

/** "Parent > Child" of the categories above a channel (not its own name); empty at the root. */
export function channelParentPath(
	tree: GuildChannelTree,
	channelId: string,
	maxChars = CHANNEL_PATH_MAX_CHARS,
): string {
	return channelPathLabel(tree, channelId, {includeSelf: false, maxChars});
}

/**
 * The categories above a channel, walked through the channel store without building the guild's tree: for a
 * one-off label (quick switcher, forward). The same rule as the tree: an unknown or non-category parent ends the
 * chain at the root, and a loop puts the channel at the root.
 */
export function channelParentPathFromStore(
	channel: {id: string; parentId: string | null},
	maxChars = CHANNEL_PATH_MAX_CHARS,
): {path: string; depth: number} {
	const names: Array<string> = [];
	const seen = new Set<string>([channel.id]);
	let parentId = channel.parentId;
	while (parentId != null && names.length <= MAX_CHANNEL_TREE_DEPTH) {
		if (seen.has(parentId)) return {path: '', depth: 0};
		seen.add(parentId);
		const parent = Channels.getChannel(parentId);
		if (!parent || parent.type !== ChannelTypes.GUILD_CATEGORY) break;
		names.unshift(parent.name ?? '');
		parentId = parent.parentId;
	}
	return {path: formatChannelPath(names, {maxChars}), depth: names.length};
}

/** "Parent > Child > name" for a channel in a picker that shows no other category label; null without a name. */
export function channelLabelWithPath(tree: GuildChannelTree, channelId: string): string | null {
	const node = tree.byId.get(channelId);
	if (!node?.channel.name) return null;
	return channelPathLabel(tree, channelId, {maxChars: CHANNEL_PATH_MAX_CHARS});
}

/** The channel's depth in the tree (0 at the root); -1 when the tree doesn't know it (a thread). */
export function channelDepth(tree: GuildChannelTree, channelId: string): number {
	return tree.byId.get(channelId)?.depth ?? -1;
}

/**
 * `items` in tree order (the sidebar's), by their channel's index in the tree. Items the tree doesn't know (threads,
 * other guilds' channels) keep their relative order after the known ones.
 */
export function sortByChannelTree<T>(
	tree: GuildChannelTree,
	items: ReadonlyArray<T>,
	channelIdOf: (item: T) => string,
): Array<T> {
	const indexOf = (item: T) => tree.byId.get(channelIdOf(item))?.index ?? Number.MAX_SAFE_INTEGER;
	return items
		.map((item, order) => ({item, order, index: indexOf(item)}))
		.sort((a, b) => a.index - b.index || a.order - b.order)
		.map((entry) => entry.item);
}

/**
 * LOCAL-ONLY: id -> sidebar position for channels of any guilds (search across communities): the guild first (in the
 * order the channels first name it), then the channel's index in that guild's tree. Unknown ids (DMs) are absent.
 */
export function channelTreeOrderIndex(
	channels: ReadonlyArray<{id: string; guildId?: string | null}>,
): Map<string, number> {
	const result = new Map<string, number>();
	const guildIds: Array<string> = [];
	for (const channel of channels) {
		if (channel.guildId != null && !guildIds.includes(channel.guildId)) guildIds.push(channel.guildId);
	}
	guildIds.forEach((guildId, guildOrdinal) => {
		const tree = guildChannelTree(guildId);
		for (const node of tree.nodes) result.set(node.id, guildOrdinal * 1_000_000 + node.index);
	});
	return result;
}

/** LOCAL-ONLY (B21): the text channels a selection can be relocated to, in sidebar order, with their category path. */
export function selectModeDestinations(
	guildId: string,
	excludeChannelId: string,
): Array<{channel: Channel; parentPath: string | null}> {
	const tree = guildChannelTree(guildId);
	return tree.nodes
		.filter((node) => node.channel.type === ChannelTypes.GUILD_TEXT && node.id !== excludeChannelId)
		.map((node) => ({channel: node.channel, parentPath: channelParentPath(tree, node.id) || null}));
}

/** A tie-break for ranked results (search, autocomplete): the tree index, unknown ids last. */
export function channelTreeIndex(tree: GuildChannelTree, channelId: string): number {
	return tree.byId.get(channelId)?.index ?? Number.MAX_SAFE_INTEGER;
}

/**
 * The channels anywhere under a category (W3), in tree order, that `isUnread` accepts, plus the unread threads
 * whose parent is one of them. Categories themselves are never acked.
 */
export function categorySubtreeUnreadIds(params: {
	tree: GuildChannelTree;
	categoryId: string;
	isUnread: (channel: Channel) => boolean;
	unreadThreads?: ReadonlyArray<{id: string; parentId: string | null}>;
}): Array<string> {
	const {tree, categoryId, isUnread} = params;
	const channelIds = channelSubtreeIds(tree, categoryId, {includeCategories: false});
	const inSubtree = new Set(channelIds);
	const ids = channelIds.filter((id) => isUnread(tree.byId.get(id)!.channel));
	for (const thread of params.unreadThreads ?? []) {
		if (thread.parentId != null && inSubtree.has(thread.parentId)) ids.push(thread.id);
	}
	return ids;
}

export const DELETE_EMPTY_CATEGORY_DESCRIPTOR = msg({
	message: 'Delete the category "{categoryName}"? Can\'t be undone.',
	comment: 'Confirm dialog body before deleting an empty channel category.',
});
const DELETE_CATEGORY_TO_TOP_DESCRIPTOR = msg({
	message:
		'Delete the category "{categoryName}"? {contents} will move to the top level, in its place. Can\'t be undone.',
	comment:
		'Confirm dialog body before deleting a channel category that is not inside another category. {contents} is a count such as "Its 3 channels and 1 category". The channels and categories directly inside it take its place in the channel list; categories inside them keep their contents.',
});
const DELETE_CATEGORY_TO_PARENT_DESCRIPTOR = msg({
	message:
		'Delete the category "{categoryName}"? {contents} will move to "{parentPath}", in its place. Can\'t be undone.',
	comment:
		'Confirm dialog body before deleting a channel category that is inside another category. {contents} is a count such as "Its 3 channels and 1 category"; {parentPath} names the category it is in, as "Parent > Child".',
});
const CONTENTS_CHANNELS_DESCRIPTOR = msg({
	message: '{channelCount, plural, one {Its # channel} other {Its # channels}}',
	comment: 'Part of the delete-category confirmation: how many channels are directly inside the category.',
});
const CONTENTS_CATEGORIES_DESCRIPTOR = msg({
	message: '{categoryCount, plural, one {Its # category} other {Its # categories}}',
	comment: 'Part of the delete-category confirmation: how many categories are directly inside the category.',
});
const CONTENTS_BOTH_DESCRIPTOR = msg({
	message:
		'{channelCount, plural, one {Its # channel} other {Its # channels}} and {categoryCount, plural, one {# category} other {# categories}}',
	comment:
		'Part of the delete-category confirmation: how many channels and categories are directly inside the category.',
});

export interface CategoryDeleteText {
	descriptor: MessageDescriptor;
	values: Record<string, string | number>;
	/** The contents part, to be translated first and passed in as `values.contents`. */
	contents: {descriptor: MessageDescriptor; values: Record<string, number>} | null;
	channelCount: number;
	categoryCount: number;
	parentPath: string | null;
}

/**
 * What deleting a category does (W2, Q4): its DIRECT children, channels and categories alike, move to its parent
 * (the top level when it has none), in its place; categories inside them keep their contents.
 */
export function categoryDeleteText(tree: GuildChannelTree, category: Channel): CategoryDeleteText {
	const node = tree.byId.get(category.id);
	const children = node?.children ?? [];
	const categoryCount = children.filter((child) => child.channel.type === ChannelTypes.GUILD_CATEGORY).length;
	const channelCount = children.length - categoryCount;
	const parentPath =
		node?.effectiveParentId != null
			? channelPathLabel(tree, node.effectiveParentId, {maxChars: CHANNEL_PATH_MAX_CHARS})
			: null;
	const categoryName = category.name ?? '';
	if (children.length === 0) {
		return {
			descriptor: DELETE_EMPTY_CATEGORY_DESCRIPTOR,
			values: {categoryName},
			contents: null,
			channelCount,
			categoryCount,
			parentPath,
		};
	}
	let contents: CategoryDeleteText['contents'] = {descriptor: CONTENTS_CATEGORIES_DESCRIPTOR, values: {categoryCount}};
	if (channelCount > 0) {
		contents =
			categoryCount > 0
				? {descriptor: CONTENTS_BOTH_DESCRIPTOR, values: {channelCount, categoryCount}}
				: {descriptor: CONTENTS_CHANNELS_DESCRIPTOR, values: {channelCount}};
	}
	return parentPath === null
		? {
				descriptor: DELETE_CATEGORY_TO_TOP_DESCRIPTOR,
				values: {categoryName},
				contents,
				channelCount,
				categoryCount,
				parentPath,
			}
		: {
				descriptor: DELETE_CATEGORY_TO_PARENT_DESCRIPTOR,
				values: {categoryName, parentPath},
				contents,
				channelCount,
				categoryCount,
				parentPath,
			};
}

/** The delete confirmation, translated. */
export function formatCategoryDeleteText(
	i18n: {_: (descriptor: MessageDescriptor, values?: Record<string, unknown>) => string},
	text: CategoryDeleteText,
): string {
	const contents = text.contents ? i18n._(text.contents.descriptor, text.contents.values) : undefined;
	return i18n._(text.descriptor, contents === undefined ? text.values : {...text.values, contents});
}
